import { useState } from 'react';
import { QUALITIES, DRONE_KEYS } from '@/lib/droneChords';
import { TIMBRES } from '@/lib/droneTimbres';
import { getEngine, playerBpm } from '@/lib/engines';
import { clickBus } from '@/lib/clickBus';
import { toB64, fromB64 } from '@/lib/tuningShare';

// A "song" = the whole timed drone: key, octave, instrument key, timbre, voicing, chords and BPM.
export const SONG_PARAM = 'song';
const KEY = 'drone.songs';
const VOICINGS = ['root', 'fifth', 'chord'];
const isInt = (v, a, b) => Number.isInteger(v) && v >= a && v <= b;

export const encodeSteps = (steps) => steps.map((s) => [s.pc, s.quality, s.bars]);
export function parseSteps(s) {
  if (!Array.isArray(s) || s.length < 1 || s.length > 16) return null;
  const ok = s.every((x) => Array.isArray(x) && isInt(x[0], 0, 11) && QUALITIES[x[1]] && isInt(x[2], 1, 8));
  return ok ? s.map(([pc, quality, bars]) => ({ pc, quality, bars })) : null;
}

export function songFromDrone(drone) {
  const { cfg, prog, timbre, voicing } = drone;
  const bpm = (prog.bpmSource || 'player') === 'player' ? playerBpm() : Math.round(getEngine('click').bpm);
  return { p: cfg.pc, o: cfg.octave, k: cfg.transpose || 'C', tm: timbre, v: voicing, t: cfg.temperament === 'just' ? 'just' : 'equal', s: encodeSteps(prog.steps), bpm };
}

// Validated song or null.
export function parseSong(x) {
  if (!x || !isInt(x.p, 0, 11) || !isInt(x.o, 1, 5) || !isInt(x.bpm, 30, 300)) return null;
  const steps = parseSteps(x.s);
  if (!steps) return null;
  return {
    pc: x.p, octave: x.o, steps, bpm: x.bpm,
    transpose: DRONE_KEYS.some((k) => k.id === x.k) ? x.k : 'C',
    timbre: TIMBRES.some((t) => t.id === x.tm) ? x.tm : 'organ',
    voicing: VOICINGS.includes(x.v) ? x.v : 'chord',
    temperament: x.t === 'just' ? 'just' : 'equal',
    name: x.n ? String(x.n).slice(0, 40) : '',
  };
}

export function applySong(drone, song) {
  const { pc, octave, transpose, timbre, voicing, temperament, steps, bpm } = song;
  drone.update({ pc, octave, transpose, timbre, voicing, temperament, prog: { ...drone.prog, enabled: true, steps, keyPc: pc, bpmSource: 'click' } });
  if (clickBus.control?.setBpm) clickBus.control.setBpm(bpm);
  else {
    let c = {};
    try { c = JSON.parse(localStorage.getItem('tuner.click') || '{}'); } catch { /* defaults */ }
    localStorage.setItem('tuner.click', JSON.stringify({ ...c, bpm }));
  }
}

export function buildSongShareUrl(song, name) {
  const url = new URL('/bordone', window.location.origin);
  url.searchParams.set(SONG_PARAM, toB64(JSON.stringify({ ...song, n: name || undefined })));
  return url.toString();
}

export function readSharedSong(search) {
  const raw = new URLSearchParams(search).get(SONG_PARAM);
  if (!raw) return null;
  try { return parseSong(JSON.parse(fromB64(raw))); } catch { return null; }
}

const loadSaved = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((x) => x && x.id && x.name && parseSong(x.song)) : [];
  } catch { return []; }
};

// Named favourite songs in localStorage; saving an existing name overwrites it.
export function useSavedSongs() {
  const [list, setList] = useState(loadSaved);
  const persist = (next) => { setList(next); localStorage.setItem(KEY, JSON.stringify(next)); };
  const save = (name, song) => {
    const clean = name.trim().slice(0, 40);
    const existing = list.find((x) => x.name.toLowerCase() === clean.toLowerCase());
    const id = existing?.id || `s${Date.now().toString(36)}`;
    persist([...list.filter((x) => x.id !== id), { id, name: clean, song }].sort((a, b) => a.name.localeCompare(b.name)));
    return id;
  };
  const remove = (id) => persist(list.filter((x) => x.id !== id));
  return { list, save, remove };
}
